import { DISPLAY_CURRENCIES } from "@shared/common/constants";
import { toFixed } from "@shared/utils/numbers";
import {
  DisplayCurrency,
  ClientData,
  MainCoinAmountData,
  GeneralAmountType,
  PrecisionType,
} from "@shared/types";
import { usePrecisions } from "@shared/utils/hooks/usePrecisions";
import { sharedLib } from "@shared/services/shared.service";
import { useMemo } from "react";
import { SkipToken, skipToken } from "@reduxjs/toolkit/query";
import { isSymbol } from "@shared/types/utils";
import { useSymbolsMetadata } from "@shared/utils/hooks/misc";

interface useMainCoinsAvailableAmountsProps {
  symbol?: string;
  exchange?: string;
  accountsArgs?: { client_id?: number; client_ids?: number[] } | SkipToken;
  accountId?: number;
  mainCoin?: string;
  skip?: boolean;
  withTotal?: boolean;
}

const useMainCoinsAvailableAmounts = ({
  symbol,
  exchange: propsExchange,
  accountId: propsAccountId,
  accountsArgs,
  mainCoin: propsMainCoin,
  skip,
  withTotal,
}: useMainCoinsAvailableAmountsProps) => {
  const { getPrecision } = usePrecisions();
  const { getMainCoin } = useSymbolsMetadata();

  const getMainCoinsAvailableAmountDataStatus =
    sharedLib.sharedApi.bots.useGetAccountsMainCoinsAvailableAmountQuery(
      skip || withTotal ? skipToken : accountsArgs
    );
  const getMainCoinsAvailableAndTotalAmountDataStatus =
    sharedLib.sharedApi.bots.useGetAccountsMainCoinsTotalAndAvailableAmountQuery(
      skip || !withTotal || isSymbol(accountsArgs)
        ? skipToken
        : {
            ...accountsArgs,
            return_total: true,
            return_client_amounts: true,
          }
    );
  const { data: mainCoinsAvailableAmountData } =
    getMainCoinsAvailableAmountDataStatus;
  const { data: mainCoinsAvailableAndTotalAmountData } =
    getMainCoinsAvailableAndTotalAmountDataStatus;

  const getAccountAmountData = (
    clientID?: number,
    amountType = GeneralAmountType.AVAILABLE
  ) => {
    if (!clientID || !withTotal) return;
    const amountsData = mainCoinsAvailableAndTotalAmountData?.data;
    const amountData = amountsData?.client?.[clientID]?.[amountType];
    if (amountData) {
      return {
        amount: amountData.USD,
        usd_amount: amountData.USD,
        btc_amount: amountData.BTC,
      };
    }
  };

  const getMainCoinAmountData = (
    clientID?: number,
    mainCoin?: string,
    amountType = GeneralAmountType.AVAILABLE
  ) => {
    if (!clientID || !mainCoin) return;
    if (withTotal) {
      const amountsData = mainCoinsAvailableAndTotalAmountData?.data;
      return amountsData?.main_coin[amountType]?.[clientID]?.[mainCoin];
    } else {
      return mainCoinsAvailableAmountData?.data?.[clientID]?.[mainCoin];
    }
  };

  const baseConvertMainCoinAmountToDisplayCurrency = (
    mainCoinAmountData: MainCoinAmountData,
    mainCoinAmount: number,
    displayCurrency: DisplayCurrency,
    applyPrecision?: boolean
  ) => {
    if (mainCoinAmountData) {
      const mainCoinAvailableAmount = mainCoinAmountData.amount || 0;
      const key =
        displayCurrency === DISPLAY_CURRENCIES.USD
          ? PrecisionType.USD_AMOUNT
          : PrecisionType.BTC_AMOUNT;
      const multiplier = mainCoinAmountData[key] / mainCoinAvailableAmount;
      const amount = mainCoinAmount * multiplier;
      return applyPrecision ? toFixed(amount, getPrecision(key) || 0) : amount;
    }
  };

  const getBaseAmountsData = (
    mainCoin?: string,
    mainCoinAmountData?: MainCoinAmountData,
    precision?: number
  ) => {
    const mainCoinAvailableAmount = mainCoinAmountData?.amount || 0;
    const convertMainCoinAmountToDisplayCurrency = (
      mainCoinAmount: number,
      displayCurrency: DisplayCurrency,
      applyPrecision?: boolean
    ) => {
      if (mainCoinAmountData) {
        return baseConvertMainCoinAmountToDisplayCurrency(
          mainCoinAmountData,
          mainCoinAmount,
          displayCurrency,
          applyPrecision
        );
      }
    };

    return {
      mainCoin,
      mainCoinAmountData,
      mainCoinAmountPrecision: precision,
      mainCoinAvailableAmount,
      convertMainCoinAmountToDisplayCurrency,
      convertMainCoinAmountToUSD: (
        mainCoinAmount: number,
        applyPrecision?: boolean
      ) => {
        return convertMainCoinAmountToDisplayCurrency(
          mainCoinAmount,
          DISPLAY_CURRENCIES.USD,
          applyPrecision
        );
      },
      convertMainCoinAmountToBTC: (
        mainCoinAmount: number,
        applyPrecision?: boolean
      ) => {
        return convertMainCoinAmountToDisplayCurrency(
          mainCoinAmount,
          DISPLAY_CURRENCIES.BTC,
          applyPrecision
        );
      },
      convertMainCoinAmountToPercent: (
        mainCoinAmount: number,
        applyPrecision?: boolean
      ) => {
        if (mainCoinsAvailableAmountData && mainCoinAvailableAmount) {
          const amount = (mainCoinAmount / mainCoinAvailableAmount) * 100;
          return applyPrecision ? toFixed(amount, 2) : amount;
        }
        return 0;
      },
    };
  };

  const getAccountAmountsData = (
    account?: ClientData,
    amountType = GeneralAmountType.AVAILABLE
  ) => {
    const mainCoin = DISPLAY_CURRENCIES.USD;
    const accountAmountData = getAccountAmountData(
      account?.client_id,
      amountType
    );
    return getBaseAmountsData(mainCoin, accountAmountData, 2);
  };

  const getMainCoinsAmountsData = (
    symbol?: string,
    _accountId?: number,
    _exchange?: string,
    amountType = GeneralAmountType.AVAILABLE
  ) => {
    const exchange = _exchange ?? propsExchange;
    const accountId = _accountId ?? propsAccountId;
    const mainCoin = propsMainCoin ?? getMainCoin(symbol, exchange);
    const mainCoinAmountData = mainCoin
      ? getMainCoinAmountData(accountId, mainCoin, amountType) ?? {
          btc_amount: 0,
          usd_amount: 0,
          amount: 0,
        }
      : undefined;
    const mainCoinAmountPrecision = getPrecision(
      PrecisionType.AMOUNT,
      symbol,
      exchange
    );
    return getBaseAmountsData(
      mainCoin,
      mainCoinAmountData,
      mainCoinAmountPrecision
    );
  };

  const mainCoinsAvailableAmountsData = useMemo(
    () => getMainCoinsAmountsData(symbol, propsAccountId, propsExchange),
    [
      mainCoinsAvailableAmountData,
      propsAccountId,
      propsExchange,
      symbol,
      getPrecision,
    ]
  );

  return {
    mainCoinsAvailableAmountsData,
    getMainCoinAmountData,
    getMainCoinsAvailableAmountDataStatus: withTotal
      ? getMainCoinsAvailableAndTotalAmountDataStatus
      : getMainCoinsAvailableAmountDataStatus,
    getAccountAmountsData,
    getMainCoinsAmountsData,
  };
};

export default useMainCoinsAvailableAmounts;
